"use client"

/* eslint-disable max-len */
import moment from "moment"
import Image from "next/image"
import Link from "next/link"
import React from "react"

import PageGrid from "../PageGrid"

import HeroSection from "./SolutionsHero"
import SubHeroSection from "./SolutionsSubHero"
import StablecoinLayout from "./StablecoinLayout"

interface attestation {
  year: number
  months: any[]
}
interface TransparencyProps {
  circulating?: string
  reserve?: string
  asOfDate?: string
  attestationsByYear?: attestation[]
}

const StablecoinTransparencyPage: React.FC<TransparencyProps> = ({
  circulating,
  reserve,
  attestationsByYear,
  asOfDate,
}) => {
  const publishedDate = asOfDate
    ? moment.utc(asOfDate).format("MM/DD/YYYY")
    : null
  return (
    <StablecoinLayout>
      <HeroSection
        headline="Ripple USD Transparency Reports"
        subHeadline="Deep relationships. Deeper stability."
      />
      <SubHeroSection
        wrapperClass="!bg-[#F3F7FA] pt-16 lg:pt-24 xl:pt-28"
        image={
          <Image
            width={1280}
            height={720}
            sizes="(min-width: 1280px) 1280px, 100vw"
            src="/illustrations/transparency-visual.jpg"
            alt="Ripple USD transparency Illustration"
            className="w-full"
          />
        }
        descriptions={[
          <span key="description-1" className="body3 md:body1 block mb-4">
            <Link
              href="/solutions/stablecoin/"
              className="text-newblue-50 hover:underline"
            >
              Ripple USD (RLUSD)
            </Link>{" "}
            is a USD stablecoin designed for institutions, created with trust
            and compliance at its core. RLUSD is backed by U.S. dollars and
            other cash equivalents, with reserves held in segregated accounts.
          </span>,
          <span
            key="description-2"
            className="body3 lg:pt-5 pt-3 md:body1 block"
          >
            Standard Custody, the issuer of RLUSD, is chartered and supervised
            by NYDFS as a limited purpose trust company, which means our
            stablecoin is required to be backed 100% by highly liquid,
            short-term, transparent reserves. Standard Custody adheres to high
            safety and soundness standards, including NYDFS customer protection
            and reserve requirements, as well as redemptions with strict SLAs.
          </span>,
        ]}
      />
      <PageGrid className="px-4 pt-28 text-gray-90 bg-white-100 ">
        <div className="col-span-full md:col-span-10 flex flex-col gap-8 md:gap-3">
          <h4 className="heading1 md:headline4">Reserve Reports</h4>
          <div className="body3 md:body2 block mb-4 pt-5">
            Ripple is committed to full transparency of the reserves supporting
            RLUSD. We provide monthly reserve reports conducted by an
            independent third-party accounting firm. These attestations validate
            the circulating supply and assets backing RLUSD.
          </div>
        </div>
      </PageGrid>
      <PageGrid
        isGrid
        className="xl:py-28 lg:py-16 md:py-9 py-10 pt-10 md:pt-10 lg:pt-10 xl:pt-10 px-4 bg-white-100"
      >
        <div className="text-gray-90 col-span-2 md:col-span-6 lg:col-span-6 mb-8 md:pr-4">
          <h4 className="heading1 md:headline4">Balances</h4>
          {circulating ? (
            <div className="p-8 rounded-lg shadow-heavy-24 mt-8 flex flex-col w-full text-center">
              <h5 className="heading3 md:heading2 mb-4">
                Total Circulating RLUSD
              </h5>
              <p className="headline3 md:headline2 blue-gradient-light">
                {circulating}
              </p>
              <h5 className="heading3 md:heading2 mb-4 mt-10">
                RLUSD Reserve Funds
              </h5>
              <p className="headline3 md:headline2 blue-gradient-light">
                {reserve}
              </p>
              <p className="mt-8 md:mt-10 text-gray-50 caption1">
                As of {publishedDate}
              </p>
            </div>
          ) : (
            <p className="text-gray-70 body3 lg:body1 mt-8">Coming Soon</p>
          )}
        </div>

        <div className="col-span-2 md:col-span-6 lg:col-span-6 text-gray-90 md:pl-4">
          <h4 className="heading1 md:headline4 mb-8">Attestations*</h4>
          <p className="w-full body3 lg:body2 text-gray-70">
            Attestations are performed by an independent Certified Public
            Accountant (CPA) licensed in the United States. The CPA issues
            monthly attestation reports pertaining to management’s assertions
            regarding the amount of RLUSD in circulation, along with information
            pertaining to the composition of the RLUSD Reserve fund. The
            attestations conducted by the CPA adhere to the attestation
            standards established by the American Institute of Certified Public
            Accountants.
          </p>
          {attestationsByYear && attestationsByYear[0].year !== null ? (
            attestationsByYear.map(({ year, months }) => (
              <div key={year} className="mt-[42px]">
                <div className="mb-4 text-heading2">{year}</div>
                <div className="grid grid-cols-4 gap-x-8 gap-y-8 mt-2 cursor-pointer">
                  {months.map((month) => (
                    <a
                      href={month.pdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={`${year}-${month.month}`}
                      className="text-left text-blue-50 body1"
                    >
                      {month.month}
                    </a>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="mt-10 mb-4 text-heading2">2024</div>
              <p className="text-gray-70 body3">Coming Soon</p>
            </>
          )}

          <div className="mt-15">
            <p className="body4 text-gray-70">
              *The CPA is an independent third-party accounting firm appointed
              in accordance with the June 8, 2022 NYDFS Guidance on the Issuance
              of U.S. Dollar-Backed Stablecoins.
            </p>
            <br />
            <p className="relative body4 text-gray-70">
              **Reports will be available monthly no more than 30 days after the
              month end.
            </p>
          </div>
        </div>
      </PageGrid>
      <section className="bg-gray-90">
        <PageGrid className="pt-9 md:pt-10 lg:pt-16 xl:pt-28 px-4">
          <p className="text-white body3 col-span-full">
            Standard Custody & Trust Company, LLC is chartered as a limited
            purpose trust company by the New York State Department of Financial
            Services to engage in virtual currency business.
          </p>
        </PageGrid>
      </section>
    </StablecoinLayout>
  )
}

export default StablecoinTransparencyPage
